import type { PaymentAmount } from '@/integrations/Immersve/payments/types';
import { isoCurrencyMinorUnits } from './isoCurrencyMinorUnits';

type Props = {
  minorUnits: string;
  currency: string;
};

const LOCALE = 'en';

export class MonetaryAmount {
  readonly minorUnits: string;
  readonly currency: string;

  private constructor(props: Props) {
    this.minorUnits = props.minorUnits;
    this.currency = props.currency;
  }

  static fromPaymentAmount(amount: PaymentAmount): MonetaryAmount {
    return new MonetaryAmount({ minorUnits: amount.value, currency: amount.currency });
  }

  format(): string {
    const fractionDigits = isoCurrencyMinorUnits[this.currency];
    const minorUnits = Number(this.minorUnits);
    if (fractionDigits === undefined || !Number.isFinite(minorUnits)) {
      return this.formatMinorUnits();
    }
    const formatter = this.currencyFormatter(fractionDigits);
    if (!formatter) {
      return this.formatMinorUnits();
    }
    return formatter.format(minorUnits / 10 ** fractionDigits);
  }

  private formatMinorUnits(): string {
    return `${this.minorUnits} ${this.currency} (minor units)`;
  }

  private currencyFormatter(fractionDigits: number): Intl.NumberFormat | undefined {
    try {
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'code',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    } catch {
      return undefined;
    }
  }
}
